import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { listPendingClaims, approveClaim, rejectClaim, type EntityClaim } from "@/api/claims";
import { requestClaimChanges } from "@/api/business-owner";

/**
 * Ownership-claim review — the admin half of the claim flow. Approving a
 * claim transfers control of a seeded provider/institution to the claimant.
 */
export default function ClaimsLab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-claims"],
    queryFn: () => listPendingClaims({ limit: 50 }),
  });
  const claims = data?.data?.claims ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Ownership Claims"
        subtitle="Approve transfers control of a seeded page to the claimant"
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Admin", href: "/admin" },
          { label: "Claims" },
        ]}
      />
      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {!isLoading && claims.length === 0 && (
        <p className="text-sm text-gray-500">No pending claims.</p>
      )}
      <ul className="space-y-3">
        {claims.map((c) => (
          <ClaimRow key={c.id} claim={c} onDone={() => qc.invalidateQueries({ queryKey: ["admin-claims"] })} />
        ))}
      </ul>
    </div>
  );
}

function ClaimRow({ claim, onDone }: { claim: EntityClaim; onDone: () => void }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function act(fn: () => Promise<unknown>, label: string) {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      setMsg(`${label} ✓`);
      onDone();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium">{claim.entityName ?? claim.entityId}</span>
        <Badge variant="purple">{claim.entityType}</Badge>
        <span className="text-sm text-gray-500">
          claimed by {claim.claimant?.name ?? "?"} ({claim.claimant?.email})
        </span>
      </div>
      {claim.claimantNote && <p className="text-sm text-gray-600">“{claim.claimantNote}”</p>}
      {claim.evidenceUrls.length > 0 && (
        <p className="text-sm">
          {claim.evidenceUrls.map((u, i) => (
            <a key={i} href={u} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline mr-3">
              evidence {i + 1} ↗
            </a>
          ))}
        </p>
      )}
      <div className="flex gap-2 items-center flex-wrap">
        <input
          className="input flex-1 min-w-48"
          placeholder="Review note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <Button size="sm" disabled={busy} onClick={() => act(() => approveClaim(claim.id, note || undefined), "Approved — ownership transferred")}>
          Approve
        </Button>
        <Button size="sm" variant="danger" disabled={busy} onClick={() => act(() => rejectClaim(claim.id, note || undefined), "Rejected")}>
          Reject
        </Button>
        <Button size="sm" variant="secondary" disabled={busy || !note.trim()} onClick={() => act(() => requestClaimChanges(claim.id, note.trim()), "Sent back — the claimant is told the note")}>Ask for more</Button>
      </div>
      {msg && <p className="text-sm text-gray-600">{msg}</p>}
    </li>
  );
}
